// Códigos de la taxonomía de trazabilidad. Espejo de GUAI.Domain/Auth/AuditEvents.cs y de las filas de
// auth.users_history_event_types: si se añade un evento hay que añadirlo en los tres sitios.
export const AuditEvents = {

    // Sesión
    SESSION_LOGIN: 'session.login',
    SESSION_LOGOUT: 'session.logout',

    // Datos del siniestro
    CASE_ACCESS: 'case.access',
    CASE_CREATE: 'case.create',
    CASE_UPDATE: 'case.update',
    CASE_FIELD_CHANGE: 'case.field_change',
    CASE_DAMAGE_TYPE_CHANGE: 'case.damage_type_change',
    CASE_STATE_CHANGE: 'case.state_change',
    CASE_ADDRESS_CHANGE: 'case.address_change',
    CASE_PROCESSOR_ASSIGN: 'case.processor_assign',
    CASE_NOTIFICATION_NUMBER_UPDATE: 'case.notification_number_update',
    CASE_CLOSE: 'case.close',
    CASE_RESUME_GENERATE: 'case.resume_generate',
    CASE_PROFIT_REVIEW_REQUIRED: 'case.profit_review_required',
    CASE_PROFIT_REVIEW_ACKNOWLEDGED: 'case.profit_review_acknowledged',

    // Citas
    APPOINTMENT_CREATE: 'appointments.create',
    APPOINTMENT_UPDATE: 'appointments.update',
    APPOINTMENT_CANCEL: 'appointments.cancel',
    APPOINTMENT_RESCHEDULE: 'appointments.reschedule',
    APPOINTMENT_STATE_CHANGE: 'appointments.state_change',
    APPOINTMENT_EVENT_CREATE: 'appointments.event_create',

    // Trabajo realizado
    JOB_CREATE: 'jobs.create',
    JOB_UPDATE: 'jobs.update',
    JOB_DELETE: 'jobs.delete',
    JOB_BILLABLE_CHANGE: 'jobs.billable_change',
    JOB_NOT_REQUIRED: 'jobs.not_required',

    // Implicados y profesionales: el código es parties.<tipo>_<acción>, ver auditPartyEvent
    PARTY_EXPERT_ADD: 'parties.expert_add',
    PARTY_EXPERT_REMOVE: 'parties.expert_remove',
    PARTY_EXPERT_ASSIGN: 'parties.expert_assign',
    PARTY_MEDIATOR_ADD: 'parties.mediator_add',
    PARTY_MEDIATOR_REMOVE: 'parties.mediator_remove',
    PARTY_MEDIATOR_ASSIGN: 'parties.mediator_assign',
    PARTY_TECHNICAL_ADD: 'parties.technical_add',
    PARTY_TECHNICAL_REMOVE: 'parties.technical_remove',
    PARTY_AFFECTED_ADD: 'parties.affected_add',
    PARTY_AFFECTED_REMOVE: 'parties.affected_remove',
    PARTY_AFFECTED_UPDATE: 'parties.affected_update',
    PARTY_AFFECTED_PROBLEM_CAUSE: 'parties.affected_problem_cause',
    PARTY_AFFECTED_TYPE: 'parties.affected_type',

    // Notas
    NOTE_CREATE: 'notes.create',
    NOTE_UPDATE: 'notes.update',
    NOTE_DELETE: 'notes.delete',

    // Adjuntos
    ATTACHMENT_UPLOAD: 'attachments.upload',
    ATTACHMENT_UPDATE: 'attachments.update',
    ATTACHMENT_DELETE: 'attachments.delete',
    ATTACHMENT_DOWNLOAD: 'attachments.download',
    ATTACHMENT_PHOTO_REPORT: 'attachments.photo_report',

    // Incidencias
    INCIDENT_CREATE: 'incidents.create',
    INCIDENT_UPDATE: 'incidents.update',
    INCIDENT_DELETE: 'incidents.delete',
    INCIDENT_APPLICABLE_CHANGE: 'incidents.applicable_change',

    // Videoperitación
    VIDEOREPORT_REQUEST: 'videoreport.request',
    VIDEOREPORT_STATUS_CHANGE: 'videoreport.status_change',

    // Chat y notificaciones
    CHAT_MESSAGE_SEND: 'chat.message_send',
    CHAT_ATTEND: 'chat.attend',
    NOTIFICATION_READ: 'notifications.read',

    // Comunicaciones enviadas
    MAIL_SEND: 'communications.mail_send',
    SMS_SEND: 'communications.sms_send',
    WHATSAPP_SEND: 'communications.whatsapp_send',

    // Comunicaciones
    CALL_OUTGOING: 'communications.call_outgoing',
    CALL_INCOMING: 'communications.call_incoming',

    // Logs de servicios. Lo emite el backend al servir el fichero, no la interfaz: así queda constancia
    // aunque alguien llame al endpoint por su cuenta. Está aquí sólo por mantener el espejo completo.
    SERVICE_LOG_DOWNLOAD: 'service_logs.download',

    // Panel de salud de la plataforma. Lo emite el backend (PlatformHealthCustomController), no la
    // interfaz: se deja aquí solo como espejo del catálogo, igual que el resto de la taxonomía.
    PLATFORM_SESSION_KILL: 'platform.session_kill'

} as const;

// Presupuestos, solicitudes de compra y facturación. Los tres cuelgan de la misma entidad en el backend
// y el módulo lo decide el tipo de transacción, así que el código se compone como módulo + acción. Hoy
// los emite el backend desde TransactionChangeTracking y TransactionLineChangeTracking; esto está aquí
// para cuando la interfaz tenga que emitir alguna acción que no deje rastro en base de datos.
export const AuditTransactionModules = {
    BUDGETS: 'budgets',
    PURCHASES: 'purchases',
    INVOICING: 'invoicing'
} as const;

export const AuditTransactionActions = {
    CREATE: 'create',
    DELETE: 'delete',
    STATE_CHANGE: 'state_change',
    ASSIGN: 'assign',
    ACCEPT: 'accept',
    DENY: 'deny',
    PAID: 'paid',
    PROFITABILITY_CHANGE: 'profitability_change',
    DELIVERY_NOTE_CREATE: 'delivery_note_create',
    REFUND_CREATE: 'refund_create',
    LINE_CREATE: 'line_create',
    LINE_UPDATE: 'line_update',
    LINE_DELETE: 'line_delete'
} as const;

export function auditTransactionEvent(module: string, action: string): string {
    return module + '.' + action;
}

// Códigos de submódulo, que se corresponden con las pestañas del acordeón del expediente.
export const AuditSubmodules = {
    CASE_DATA: 'caseData',
    APPOINTMENTS: 'appointments',
    EVENTS: 'events',
    JOBS: 'jobs',
    BUDGETS: 'budgets',
    ATTACHMENTS: 'attachments',
    AFFECTED: 'affected',
    EXPERTS: 'experts',
    TECHNICALS: 'technicals',
    MEDIATORS: 'mediators',
    NOTES: 'notes',
    PURCHASES: 'purchases',
    INCIDENTS: 'incidents',
    CLAIMS: 'claims',
    REFUNDS: 'refunds'
} as const;

/// Datos opcionales que acompañan a un evento de actividad.
export interface AuditEventData {
    /// Expediente al que pertenece la acción. Si no se indica, se toma del contexto activo.
    caseId?: string | null;

    /// Tipo de la entidad afectada, por ejemplo 'caseService' o 'transaction'.
    entityType?: string | null;

    /// Identificador de la entidad afectada.
    entityId?: string | null;

    /// Campo modificado, cuando la acción es un cambio de valor.
    fieldName?: string | null;

    previousValue?: any;

    newValue?: any;

    /// Motivo indicado por el usuario (cancelación, denegación...).
    reason?: string | null;

    /// Contexto adicional del evento: especialidad, tipología, número de presupuesto, destinatario...
    metadata?: { [key: string]: any };

    /// Descripción ya construida. Si no se indica, el backend la genera desde la plantilla del catálogo.
    information?: string | null;
}

/// Evento tal y como viaja al backend. Coincide con UserActivityEventDto.
export interface AuditEvent {
    eventCode: string;
    date: string;
    caseId?: string | null;
    entityType?: string | null;
    entityId?: string | null;
    fieldName?: string | null;
    previousValue?: string | null;
    newValue?: string | null;
    reason?: string | null;
    metadata?: { [key: string]: string } | null;
    information?: string | null;
    sessionId?: string | null;
    correlationId?: string | null;
}
